import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronDown, MapPin, Home, ExternalLink } from "react-feather";
import { money } from "../../../dashboard/dashboardHelpers";
import { moduleMeta } from "../oneStopModules";
import { drillTarget, isListOnlyDrill } from "../oneStopDrill";
import EmployeeAvatar from "./EmployeeAvatar";

const INDENT_STEP = 17;

const qty = (n) => {
  const v = Number(n) || 0;
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sign}${Math.abs(v).toLocaleString("en-KE", { maximumFractionDigits: 2 })}`;
};

const fmtValue = (value, isMoney) => (isMoney ? money(value) : qty(value));

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const STATUS_CLASS = (status, isVoided) => {
  if (isVoided) return "badge bg-secondary-subtle text-secondary";
  const s = (status || "").toUpperCase();
  if (["BANKED", "CLOSED", "APPROVED", "PAID", "POSTED", "ISSUED", "ACTIVE", "CURRENT", "PRESENT", "SYNCED", "CONFIRMED", "RECORDED", "IN WARRANTY"].includes(s))
    return "badge bg-success-subtle text-success";
  if (["SUBMITTED", "NOT BANKED", "PENDING", "POSTPONED", "ON_HOLD", "ABSENT", "TAKEN"].includes(s))
    return "badge bg-warning-subtle text-warning";
  if (["REJECTED", "CANCELLED", "LOST", "DELETED", "ENDED"].includes(s))
    return "badge bg-danger-subtle text-danger";
  return "badge bg-light text-dark border";
};

const dirClass = (direction) =>
  direction === "IN" ? "dir-in" : direction === "OUT" ? "dir-out" : "dir-none";

const amtClass = (direction) =>
  direction === "IN" ? "onestop-amt-in" : direction === "OUT" ? "onestop-amt-out" : "";

/**
 * One row of the shared column grid. Every depth renders through this, which is
 * what keeps the Amount/Count columns aligned - depth only changes the indent
 * inside the name cell, never the grid itself.
 */
const TreeRow = ({
  depth,
  isOpen,
  onToggle,
  icon,
  name,
  sub,
  extra,
  value,
  isMoney,
  count,
  moduleColor,
  levelClass,
  direction,
}) => (
  <button
    type="button"
    className={`onestop-row ${levelClass}`}
    style={{ "--onestop-ind": `${depth * INDENT_STEP}px`, "--onestop-mc": moduleColor || "transparent" }}
    aria-expanded={isOpen}
    onClick={onToggle}
  >
    <span className="onestop-rail" />
    <span />
    <span className="onestop-caret">
      {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
    </span>
    <span className="onestop-ic">{icon}</span>
    <span className="onestop-name">
      <span className="onestop-n1">
        {name}
        {extra}
      </span>
      {sub ? <span className="onestop-n2">{sub}</span> : null}
    </span>
    <span className={`onestop-amt ${amtClass(direction)}`}>{fmtValue(value, isMoney)}</span>
    <span className="onestop-cnt">{count}</span>
  </button>
);

const TransactionTable = ({ type, isMoney, depth, moduleColor, photoUrls }) => (
  <div
    className="onestop-txblock"
    style={{ "--onestop-ind": `${(depth + 1) * INDENT_STEP}px`, "--onestop-mc": moduleColor }}
  >
    <span className="onestop-rail" />
    <span />
    <div className="onestop-txscroll">
      <table className="onestop-tx">
        <thead>
          <tr>
            <th>Date</th>
            <th>Transaction no.</th>
            <th>Detail</th>
            <th style={{ textAlign: "right" }}>{isMoney ? "Amount" : "Qty"}</th>
            <th>Status</th>
            <th>Drill down</th>
          </tr>
        </thead>
        <tbody>
          {type.rows.map((row, i) => {
            const target = drillTarget(row);
            const raw = isMoney ? row.amount : row.quantity;
            return (
              <tr key={`${row.drillGuid || row.transactionNumber || "r"}-${i}`}>
                <td className="onestop-num">{fmtDate(row.transactionDate)}</td>
                <td className={`onestop-num ${row.isVoided ? "onestop-voided" : ""}`}>
                  {row.transactionNumber || <span className="text-muted">&mdash;</span>}
                </td>
                <td className="text-body-secondary">
                  {row.counterpartyEmployeeRecordGuid ? (
                    <span className="onestop-whocell">
                      <EmployeeAvatar
                        name={row.counterpartyName}
                        photoUrl={
                          row.counterpartyHasPhoto
                            ? photoUrls?.[row.counterpartyEmployeeRecordGuid]
                            : null
                        }
                      />
                      <span>{row.detail || row.counterpartyName || "—"}</span>
                    </span>
                  ) : (
                    row.detail || <span className="text-muted">&mdash;</span>
                  )}
                </td>
                <td className={`onestop-money ${raw == null ? "" : amtClass(row.direction)}`}>
                  {raw == null ? (
                    <span className="text-muted fw-normal">&mdash;</span>
                  ) : (
                    fmtValue(raw, isMoney)
                  )}
                  {/* An LPO never moved stock - only the Stock-In raised against
                      it did - so its quantity is labelled rather than coloured
                      like a real movement. */}
                  {row.direction === "NONE" && row.transactionType.startsWith("LPO") && !isMoney ? (
                    <div className="text-muted fw-normal" style={{ fontSize: 10 }}>
                      ordered only
                    </div>
                  ) : null}
                </td>
                <td>
                  <span className={STATUS_CLASS(row.status, row.isVoided)}>
                    {row.isVoided ? row.status || "Voided" : row.status || "—"}
                  </span>
                </td>
                <td>
                  {row.isAggregate ? (
                    <span className="text-muted" style={{ fontSize: 11 }}>
                      summary of many
                    </span>
                  ) : target ? (
                    <Link to={target} className="d-inline-flex align-items-center gap-1">
                      <ExternalLink size={12} />
                      {isListOnlyDrill(row) ? "Open list" : "Open"}
                    </Link>
                  ) : (
                    <span className="text-muted" style={{ fontSize: 11 }}>
                      no screen yet
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const OneStopTree = ({ tree, isMoney, photoUrls }) => {
  // Branches start open, everything below starts closed - the summary is the
  // point of the first look; the detail is a deliberate click.
  const [open, setOpen] = useState(() => new Set(tree.map((b) => b.key)));

  const toggle = (key) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div>
      {tree.map((branch) => {
        const branchOpen = open.has(branch.key);
        return (
          <section className="onestop-branch" key={branch.key}>
            <TreeRow
              depth={0}
              levelClass="lv-branch"
              isOpen={branchOpen}
              onToggle={() => toggle(branch.key)}
              icon={<Home size={14} className="text-danger" />}
              name={branch.branchName}
              sub={branch.branchCode}
              value={branch.totals.value}
              isMoney={isMoney}
              count={`${branch.totals.count} transactions`}
            />

            {branchOpen && (
              <>
                <div className="onestop-caps">
                  <span className="onestop-caps-a">{isMoney ? "Amount" : "Quantity"}</span>
                  <span className="onestop-caps-c">Txns</span>
                </div>

                {branch.sites.map((site) => {
                  const siteOpen = open.has(site.key);
                  const showSite = branch.hasSites && site.siteLabel;
                  const baseDepth = showSite ? 2 : 1;

                  const modules = site.modules.map((mod) => {
                    const modOpen = open.has(mod.key);
                    const meta = moduleMeta(mod.moduleCode);
                    const color = meta.light;
                    const Icon = meta.Icon;
                    return (
                      <React.Fragment key={mod.key}>
                        <TreeRow
                          depth={baseDepth}
                          levelClass="lv-module"
                          isOpen={modOpen}
                          onToggle={() => toggle(mod.key)}
                          icon={
                            <span className="onestop-mchip" style={{ "--onestop-mc": color }}>
                              <Icon size={13} />
                            </span>
                          }
                          name={mod.moduleName}
                          value={mod.totals.value}
                          isMoney={isMoney}
                          count={`${mod.totals.count} txns`}
                          moduleColor={color}
                        />
                        {modOpen &&
                          mod.types.map((type) => {
                            const typeOpen = open.has(type.key);
                            return (
                              <React.Fragment key={type.key}>
                                <TreeRow
                                  depth={baseDepth + 1}
                                  levelClass="lv-type"
                                  isOpen={typeOpen}
                                  onToggle={() => toggle(type.key)}
                                  icon={null}
                                  name={type.transactionType}
                                  sub={
                                    type.direction === "IN" || type.direction === "OUT"
                                      ? null
                                      : "for reference - paid or counted on another row, not added to totals"
                                  }
                                  extra={
                                    <span className={`onestop-dir ${dirClass(type.direction)}`}>
                                      {type.direction === "IN"
                                        ? "IN"
                                        : type.direction === "OUT"
                                        ? "OUT"
                                        : "—"}
                                    </span>
                                  }
                                  value={
                                    type.direction === "IN" || type.direction === "OUT"
                                      ? type.totals.value
                                      : type.totals.referenceValue
                                  }
                                  isMoney={isMoney}
                                  count={`${type.totals.count} txns`}
                                  moduleColor={color}
                                  direction={type.direction}
                                />
                                {typeOpen && (
                                  <TransactionTable
                                    type={type}
                                    isMoney={isMoney}
                                    depth={baseDepth + 1}
                                    moduleColor={color}
                                    photoUrls={photoUrls}
                                  />
                                )}
                              </React.Fragment>
                            );
                          })}
                      </React.Fragment>
                    );
                  });

                  if (!showSite) return <React.Fragment key={site.key}>{modules}</React.Fragment>;

                  return (
                    <React.Fragment key={site.key}>
                      <TreeRow
                        depth={1}
                        levelClass="lv-site"
                        isOpen={siteOpen}
                        onToggle={() => toggle(site.key)}
                        icon={<MapPin size={13} className="text-danger" />}
                        name={site.siteLabel}
                        sub={site.locationId ? `LocationId ${site.locationId}` : null}
                        value={site.totals.value}
                        isMoney={isMoney}
                        count={`${site.totals.count} txns`}
                      />
                      {siteOpen && modules}
                    </React.Fragment>
                  );
                })}

                {branch.quietModules.length > 0 && (
                  <div className="onestop-quiet">
                    <span className="onestop-quiet-label">No activity in period</span>
                    {branch.quietModules.map((code) => (
                      <span key={code} className="badge bg-light text-secondary border fw-normal">
                        {moduleMeta(code).label}
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default OneStopTree;
